import React from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import styled, { css } from 'styled-components'

import { Arrow, Button, Spinner, Text, baseColors } from '@fairhq/common'
import { ArrowDirection } from '@fairhq/common/lib/components/atoms/icons/Arrow'
import { WhiteBoxWithShadow } from 'components/WhiteCardWithShadow'
import { Layout } from 'layout/Layout'

import { AreaType } from 'store/area/types'
import { useGetAuditAreasQuery } from 'store/audit/auditApiWithQuery'
import { useAppSelector } from 'store/hooks'
import { State } from 'store/state'
import { QueryStatus } from 'store/types'

import { useLevelAndCertification } from './hooks/useLevelAndCertification'
import { SubAreaCard } from './SubAreaCard'

const TitleAndCertification = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
  }
`

const Container = styled.main`
  display: grid;
  grid-template-columns: 0.5fr 1fr;
  gap: 40px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const LevelBox = styled(WhiteBoxWithShadow)<{ withAccentBackground: boolean }>`
  flex-direction: column;
  gap: 4px;
  align-self: flex-start;
  padding: 24px;
  border-radius: 12px;
  ${({ withAccentBackground }) =>
    withAccentBackground &&
    css`
      background-color: ${baseColors.backgroundsPrimarySurface};
    `}
`

const GrowColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ImageWrapper = styled.div`
  display: flex;
  padding: 0 40px;
  justify-content: center;
  align-items: center;
  height: 100%;
`
const StyledSubHeader = styled(Text)`
  text-transform: uppercase;
`

const Image = styled.img`
  display: flex;
  width: 280px;
  height: 280px;
  justify-content: center;
  align-items: center;
`

const ButtonContainer = styled.div`
  align-self: flex-start;
  margin-top: 24px;
`

const HeaderBlock = styled.div<{ withSpaceTop: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ withSpaceTop }) =>
    withSpaceTop &&
    css`
      padding-top: 120px;
      @media (max-width: 900px) {
        padding-top: 0;
      }
    `}
`

const NoMarginText = styled(Text)`
  margin: 0;
`
export const ReportsEquality = () => {
  const { sessionId } = useAppSelector(
    (state: State) => ({
      sessionId: state.apiHeadersReducer.sessionId,
    }),
    shallowEqual
  )

  const { data: areas } = useGetAuditAreasQuery({
    sessionId,
    query: { type: AreaType.SUB },
  })

  const { t } = useTranslation()

  const {
    queryStatus,
    certification,
    levelInfo,
    labelsBase,
    isReadyForCertification,
  } = useLevelAndCertification()

  const relevantAreasCode = levelInfo?.subAreas
    ? levelInfo?.subAreas.map(area => area.code)
    : []

  const relevantFullAreas = areas?.filter(area =>
    relevantAreasCode.includes(area.code)
  )

  return (
    <>
      {levelInfo && (
        <Layout
          contentSx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}
          dark
        >
          <TitleAndCertification>
            <HeaderBlock withSpaceTop={!!certification}>
              <Text fontWeight={700} size="xl" element="h1" type="heading">
                {t(`equality.title`)}
              </Text>
              <Text fontWeight={400} size="sm" element="p" type="body">
                {t(`reports.equality.description`)}
              </Text>
            </HeaderBlock>
            {certification && (
              <ImageWrapper>
                <Image
                  src={`https://static.charliehr.com/certificates/${certification.level}.svg`}
                  alt={`The certification stamp, level ${t(
                    `reports.level.level_${levelInfo.levelNumber}`
                  )}`}
                />
              </ImageWrapper>
            )}
          </TitleAndCertification>
          {queryStatus === QueryStatus.LOADING && <Spinner center />}

          {queryStatus === QueryStatus.SUCCESS && (
            <Container>
              <LevelBox withAccentBackground={isReadyForCertification ?? false}>
                <StyledSubHeader
                  fontWeight={700}
                  size="2xs"
                  element="p"
                  type="body"
                >
                  {t(`${labelsBase}.secondaryTitle`)}
                </StyledSubHeader>

                <Text fontWeight={700} size="xl" element="h2" type="heading">
                  <Trans
                    i18nKey={`reports.level.level_${levelInfo.levelNumber}`}
                  />
                </Text>
                {!isReadyForCertification &&
                  +(levelInfo.completionPercentage ?? 0) > 0 && (
                    <Text
                      fontWeight={600}
                      size="lg"
                      element="p"
                      type="body"
                      color={baseColors.contentContent}
                    >
                      <Trans
                        i18nKey={`${labelsBase}.subTitle`}
                        values={{
                          completionPercentage: levelInfo.completionPercentage,
                        }}
                      />
                    </Text>
                  )}

                <NoMarginText
                  fontWeight={400}
                  size="sm"
                  element="p"
                  type="body"
                >
                  <Trans
                    i18nKey={`${labelsBase}.description`}
                    values={{
                      level: levelInfo.levelNumber,
                    }}
                  />
                </NoMarginText>
                {isReadyForCertification && (
                  <ButtonContainer>
                    <Button
                      as="a"
                      href={t(`${labelsBase}.link`)}
                      iconRight={<Arrow direction={ArrowDirection.RIGHT} />}
                    >
                      {t(`${labelsBase}.button`)}
                    </Button>
                  </ButtonContainer>
                )}
              </LevelBox>
              {levelInfo.subAreas.length > 0 &&
                relevantFullAreas &&
                relevantFullAreas.length > 0 && (
                  <GrowColumn>
                    {relevantFullAreas.map(area => {
                      const relevantLevelInfoArea = levelInfo.subAreas.find(
                        subArea => subArea.code === area.code
                      )

                      return (
                        relevantLevelInfoArea && (
                          <SubAreaCard
                            key={area.code}
                            subArea={area}
                            subAreaCompletion={
                              relevantLevelInfoArea?.completionPercentage
                            }
                          />
                        )
                      )
                    })}
                  </GrowColumn>
                )}
            </Container>
          )}
        </Layout>
      )}
    </>
  )
}
